if (!customElements.get('blog-list-v2')) {
  const SCROLL_TOLERANCE = 2;

  /**
   * Horizontal blog-card list with overflow arrows (TA/PC).
   */
  class BlogListV2 extends HTMLElement {
    connectedCallback() {
      if (this.isBound) this.unbind();

      this.viewport = this.querySelector('[data-blv2-viewport]');
      this.track = this.querySelector('[data-blv2-track]');
      this.prevBtn = this.querySelector('[data-blv2-prev]');
      this.nextBtn = this.querySelector('[data-blv2-next]');

      if (!this.viewport || !this.track) return;

      this.syncFrame = null;
      this.arrowMq = window.matchMedia('(min-width: 768px)');

      this.onPrevClick = () => this.scrollByItem(-1);
      this.onNextClick = () => this.scrollByItem(1);
      this.onScroll = () => this.queueArrowSync();
      this.onArrowMqChange = () => this.queueArrowSync();

      this.prevBtn?.addEventListener('click', this.onPrevClick);
      this.nextBtn?.addEventListener('click', this.onNextClick);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.arrowMq.addEventListener) {
        this.arrowMq.addEventListener('change', this.onArrowMqChange);
      } else {
        this.arrowMq.addListener(this.onArrowMqChange);
      }

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.queueArrowSync());
        this.resizeObserver.observe(this.viewport);
        this.resizeObserver.observe(this.track);
      }

      this.isBound = true;
      this.queueArrowSync();
    }

    disconnectedCallback() {
      this.unbind();
    }

    /**
     * Removes listeners and observers.
     */
    unbind() {
      this.prevBtn?.removeEventListener('click', this.onPrevClick);
      this.nextBtn?.removeEventListener('click', this.onNextClick);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();

      if (this.arrowMq) {
        if (this.arrowMq.removeEventListener) {
          this.arrowMq.removeEventListener('change', this.onArrowMqChange);
        } else {
          this.arrowMq.removeListener(this.onArrowMqChange);
        }
      }

      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = null;
      this.resizeObserver = null;
      this.isBound = false;
    }

    /**
     * Coalesces scroll/resize into one arrow visibility pass.
     */
    queueArrowSync() {
      if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.syncArrows();
      });
    }

    /**
     * Shows prev/next only on TA/PC when that direction can still scroll.
     */
    syncArrows() {
      if (!this.prevBtn || !this.nextBtn) return;

      if (!this.arrowMq?.matches) {
        this.prevBtn.hidden = true;
        this.nextBtn.hidden = true;
        return;
      }

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const canScroll = maxScroll > SCROLL_TOLERANCE;
      if (!canScroll) {
        this.prevBtn.hidden = true;
        this.nextBtn.hidden = true;
        return;
      }

      this.prevBtn.hidden = this.viewport.scrollLeft <= SCROLL_TOLERANCE;
      this.nextBtn.hidden = this.viewport.scrollLeft >= maxScroll - SCROLL_TOLERANCE;
    }

    /**
     * @param {-1 | 1} direction
     */
    scrollByItem(direction) {
      const firstItem = Array.from(this.track.children).find((item) => {
        return !item.hidden && getComputedStyle(item).display !== 'none';
      });
      const itemWidth = firstItem?.getBoundingClientRect().width || 0;
      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const step = itemWidth + gap;
      if (step <= 0) return;

      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const targetLeft = Math.min(maxScroll, Math.max(0, this.viewport.scrollLeft + direction * step));
      this.viewport.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }
  }

  customElements.define('blog-list-v2', BlogListV2);
}
